import { useState } from "react";
import axios from "axios";
import { ArrowRight, BellRing, CheckCircle2 } from "lucide-react";
import { API } from "../../lib/eventUtils";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "" };

export const WaitlistForm = ({ slug, waitlistCount, onJoined }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/events/${slug}/waitlist`, form);
      setDone(data);
      onJoined?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="waitlist-success">
        <CheckCircle2 size={44} className="text-volt mx-auto mb-4" />
        <h3 className="font-head font-extrabold uppercase text-2xl mb-2">You're on the waitlist</h3>
        <p className="text-zinc-400 text-sm leading-relaxed">
          You're <span className="text-volt font-semibold">#{done.position}</span> in line. The moment a spot
          opens we'll email you — first come, first served.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="waitlist-form">
      <div className="flex items-start gap-3 mb-5">
        <BellRing size={18} className="text-volt mt-0.5 shrink-0" />
        <p className="text-zinc-400 text-sm leading-relaxed">
          This session is sold out. Join the waitlist and we'll email you instantly if a spot opens.
          {waitlistCount > 0 && (
            <span className="block mt-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="waitlist-count">
              {waitlistCount} {waitlistCount === 1 ? "athlete" : "athletes"} already in line
            </span>
          )}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">First Name *</span>
          <input required value={form.first_name} onChange={set("first_name")} placeholder="Alex" className="volt-input" data-testid="waitlist-first-name" />
        </label>
        <label className="block">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Last Name *</span>
          <input required value={form.last_name} onChange={set("last_name")} placeholder="Rivera" className="volt-input" data-testid="waitlist-last-name" />
        </label>
      </div>
      <label className="block mb-4">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Email *</span>
        <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="volt-input" data-testid="waitlist-email" />
      </label>
      <label className="block mb-5">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Phone *</span>
        <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(214) 555-0123" className="volt-input" data-testid="waitlist-phone" />
      </label>
      {error && (
        <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-4" data-testid="waitlist-error">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy} className="btn-volt w-full justify-center disabled:opacity-60" data-testid="waitlist-submit">
        {busy ? "Joining…" : "Join the Waitlist"} {!busy && <ArrowRight size={16} />}
      </button>
      <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-zinc-600 mt-4 text-center">
        Free to join · instant email alert when a spot opens
      </p>
    </form>
  );
};
